import * as z from "zod";

/**
 * What Gale says should happen about a KYC issue (gale-apis
 * `AdyenOnboardingIssues`). `action` on the wire stays a free string so an
 * action Gale adds later parses; readers treat an unknown or absent action
 * like `SUPPORT`: real for the SE, not something the merchant can fix alone.
 */
export enum GaleKycIssueAction {
  /** The merchant opens a fresh hosted link and continues verification. */
  RegenerateLink = "REGENERATE_LINK",
  /** The merchant re-uploads or corrects something on the hosted page. */
  Resubmit = "RESUBMIT",
  /** Only Gale or Redo can act (MCC mapping, agreement, suspension). */
  Support = "SUPPORT",
  /** Still settling on Gale's side; it may clear by itself. */
  Wait = "WAIT",
  /** A stale read Gale's own sweep repairs (`VERIFICATION_SLOW`). */
  Refresh = "REFRESH",
}

/** Actions the watcher never surfaces: Gale is still working on them. */
const TRANSIENT_KYC_ISSUE_ACTIONS: ReadonlySet<string> = new Set([
  GaleKycIssueAction.Wait,
  GaleKycIssueAction.Refresh,
]);

const MERCHANT_FIXABLE_KYC_ISSUE_ACTIONS: ReadonlySet<string> = new Set([
  GaleKycIssueAction.RegenerateLink,
  GaleKycIssueAction.Resubmit,
]);

/**
 * Codes the watcher never reports, whatever action Gale attaches. Gale
 * reports an expired hosted onboarding link as `ONBOARDING_LINK_EXPIRED`
 * (`REGENERATE_LINK`), but nobody has to act on it: the Redo KYC link
 * (`galeKycPath`) opens a fresh hosted page every time, so the merchant is
 * never stuck on an expired one. It is also common (every merchant who pauses
 * mid-verification past the link's lifetime) and comes and goes, since Gale
 * hides it while another `REGENERATE_LINK` issue is open, so alerting on it
 * would post and email repeatedly about nothing.
 */
const IGNORED_KYC_ISSUE_CODES: ReadonlySet<string> = new Set([
  "ONBOARDING_LINK_EXPIRED",
]);

/** Whether an issue is something a person has to act on (SE or merchant). */
export function isActionableGaleKycIssue(
  issue: Pick<HsaGaleOnboardingIssue, "code" | "action">,
): boolean {
  if (IGNORED_KYC_ISSUE_CODES.has(issue.code)) {
    return false;
  }
  return !issue.action || !TRANSIENT_KYC_ISSUE_ACTIONS.has(issue.action);
}

/** Whether the merchant can clear the issue from the hosted KYC page alone. */
export function isMerchantFixableGaleKycIssueAction(
  action: string | null | undefined,
): boolean {
  return !!action && MERCHANT_FIXABLE_KYC_ISSUE_ACTIONS.has(action);
}

/** One merchant-safe KYC issue as Gale reports it. Never developer text. */
export const hsaGaleOnboardingIssueSchema = z.object({
  code: z.string(),
  message: z.string(),
  action: z.string().nullable(),
  occurredAt: z.string().nullable(),
});
export type HsaGaleOnboardingIssue = z.infer<
  typeof hsaGaleOnboardingIssueSchema
>;

/**
 * Kind-specific facts stored on a Gale onboarding event. Every member is
 * optional so a row written by a newer watcher still parses for an older
 * reader. `issues` is for the KYC kinds (the full actionable list at the time
 * of the event); `raisedCodes` and `resolvedCodes` are the diff that produced
 * a KYC issue event; `productCount` and `eligibilityPushedAt` are for the
 * catalog kinds; the other kinds carry nothing.
 */
export const hsaGaleOnboardingEventDetailsSchema = z.object({
  issues: z.array(hsaGaleOnboardingIssueSchema).optional(),
  raisedCodes: z.array(z.string()).optional(),
  resolvedCodes: z.array(z.string()).optional(),
  productCount: z.number().int().optional(),
  eligibilityPushedAt: z.string().optional(),
});
export type HsaGaleOnboardingEventDetails = z.infer<
  typeof hsaGaleOnboardingEventDetailsSchema
>;

/** A catalog event's variant count as both the admin card and Slack show it. */
export function formatGaleCatalogVariantCount(count: number): string {
  return `${count} variant${count === 1 ? "" : "s"}`;
}
